import React, { useEffect, useState } from "react";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import { useFetchBchDailyReportMutation } from "../../../features/tenant/Bch/BchApi";
import toast from "react-hot-toast";
import { CircleLoaderComponent } from "../../../components/Loader/CircleLoaderComponent";
import { BiQrScan, BiError, BiTransfer, BiCalendar } from "react-icons/bi";
import { MdReceipt } from "react-icons/md";

const BchDailyReport = () => {
  const [tableDataItems, setTableDataItems] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationResetToggle, setPaginationResetToggle] = useState(false);

  // Default date filter to last 30 days
  const getPastDateStr = (days) => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString().split("T")[0];
  };

  const initialRequest = {
    limit: 10,
    offset: 0,
    startDate: getPastDateStr(30),
    endDate: new Date().toISOString().split("T")[0],
  };

  const [request, setRequest] = useState(initialRequest);
  const [totals, setTotals] = useState({
    totalHits: 0,
    successQrs: 0,
    errorQrs: 0,
  });

  const [
    fetchBchReport,
    {
      data: reportResponse,
      error: reportError,
      isLoading: reportLoading,
    },
  ] = useFetchBchDailyReportMutation();

  const handleFetchReport = (currentRequest = request) => {
    fetchBchReport({
      tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
      token: sessionStorage.getItem("access_token"),
      body: currentRequest,
    });
  };

  useEffect(() => {
    handleFetchReport();
  }, [limit, offset]);

  useEffect(() => {
    if (reportResponse && reportResponse.body) {
      toast.dismiss();
      const arr = reportResponse.body.summary || [];
      setTableDataItems(arr);
      setTotalRows(reportResponse.body.total_records || 0);
      // it returns `summary` (paginated) and `total_records`.

      const overall = reportResponse.body.overall_totals || {
        total_hits: 0,
        success_qrs: 0,
        error_qrs: 0,
      };

      setTotals({
        totalHits: overall.total_hits,
        successQrs: overall.success_qrs,
        errorQrs: overall.error_qrs,
      });
    }

    if (reportError) {
      toast.dismiss();
      toast.error(
        reportError?.data?.message || "Failed to fetch report data",
        { id: "reportError" }
      );
    }
  }, [reportResponse, reportError]);

  const clearFilters = () => {
    setRequest(initialRequest);
    setLimit(10);
    setOffset(0);
    setCurrentPage(1);
    setPaginationResetToggle((prev) => !prev);
    
    // Trigger fetch with initial request
    fetchBchReport({
      tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
      token: sessionStorage.getItem("access_token"),
      body: initialRequest,
    });
  };

  const handleSearch = () => {
    setOffset(0);
    setCurrentPage(1);
    setPaginationResetToggle((prev) => !prev);
    handleFetchReport({ ...request, offset: 0 });
  };

  const columns = [
    {
      name: "S.No",
      cell: (row, idx) => offset + Number(idx + 1),
      sortable: true,
      width: "80px",
    },
    {
      name: "Date",
      selector: (row) => row.date,
      sortable: true,
      width: "140px",
    },
    {
      name: "Total Hits",
      selector: (row) => row.total_hits,
      sortable: true,
      center: true,
    },
    {
      name: "Success QRs",
      selector: (row) => row.success_qrs,
      sortable: true,
      center: true,
    },
    {
      name: "Error QRs",
      selector: (row) => row.error_qrs,
      sortable: true,
      center: true,
    },
  ];

  const handlePageChange = (pageNumber) => {
    const newOffset = (pageNumber - 1) * limit;
    setOffset(newOffset);
    setRequest((prev) => ({ ...prev, offset: newOffset }));
    setCurrentPage(pageNumber);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setRequest((prev) => ({
      ...prev,
      limit: newPerPage,
      offset: 0,
    }));
    setLimit(newPerPage);
    setOffset(0);
    setCurrentPage(1);
    setPaginationResetToggle((prev) => !prev);
  };

  return (
    <>
      <div className="flex w-full bg-gray-100 min-h-screen justify-center pb-8 font-sans">
        <div className="w-full max-w-[1600px] px-6">
          
          {/* Header */}
          <div className="border-b bg-white border-gray-200 -mx-6 px-6 py-4 shadow-sm mb-6 flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-extrabold text-gray-800 tracking-tight">
                BCH API Integration Report
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Monitor and analyze QRs & invoices received via BCH API endpoints.
              </p>
            </div>
            <LinksComponent />
          </div>

          {/* Aggregated Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            
            {/* Total Hits */}
            <div className="bg-gradient-to-br from-indigo-500 to-indigo-700 rounded-xl p-3.5 px-4 text-white shadow-lg transition-transform duration-300 hover:-translate-y-1 hover:shadow-xl relative overflow-hidden">
              <div className="absolute right-3 top-2.5 opacity-20 text-4xl">
                <BiTransfer />
              </div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-indigo-100">
                Total API Hits
              </p>
              <h3 className="text-2xl font-extrabold mt-1">
                {(totals.totalHits || 0).toLocaleString()}
              </h3>
            </div>

            {/* Success QRs */}
            <div className="bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl p-3.5 px-4 text-white shadow-lg transition-transform duration-300 hover:-translate-y-1 hover:shadow-xl relative overflow-hidden">
              <div className="absolute right-3 top-2.5 opacity-20 text-4xl">
                <BiQrScan />
              </div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-blue-100">
                Success QRs
              </p>
              <h3 className="text-2xl font-extrabold mt-1">
                {(totals.successQrs || 0).toLocaleString()}
              </h3>
            </div>

            {/* Error QRs */}
            <div className="bg-gradient-to-br from-rose-500 to-red-600 rounded-xl p-3.5 px-4 text-white shadow-lg transition-transform duration-300 hover:-translate-y-1 hover:shadow-xl relative overflow-hidden">
              <div className="absolute right-3 top-2.5 opacity-20 text-4xl">
                <BiError />
              </div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-rose-100">
                Error QRs
              </p>
              <h3 className="text-2xl font-extrabold mt-1">
                {(totals.errorQrs || 0).toLocaleString()}
              </h3>
            </div>

          </div>

          {/* Date Filter Panel */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm mb-8">
            <h2 className="text-md font-bold text-gray-700 mb-4 flex items-center gap-2">
              <BiCalendar className="text-indigo-600 text-lg" />
              Filter by Date Range
            </h2>
            <div className="flex flex-wrap gap-4 items-end">
              
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  className="w-full px-4 py-2.5 text-sm text-gray-700 border border-gray-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition duration-150"
                  value={request.startDate}
                  onChange={(e) => {
                    const val = e.target.value;
                    setRequest((prev) => ({ ...prev, startDate: val }));
                  }}
                />
              </div>

              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  className="w-full px-4 py-2.5 text-sm text-gray-700 border border-gray-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition duration-150"
                  value={request.endDate}
                  onChange={(e) => {
                    const val = e.target.value;
                    setRequest((prev) => ({ ...prev, endDate: val }));
                  }}
                />
              </div>

              <div className="flex gap-2">
                <button
                  className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-xl shadow-md shadow-indigo-600/10 hover:shadow-lg transition duration-150"
                  onClick={handleSearch}
                >
                  Apply Filters
                </button>
                <button
                  className="px-6 py-2.5 border border-gray-300 hover:bg-gray-55 text-gray-700 text-sm font-semibold rounded-xl transition duration-150"
                  onClick={clearFilters}
                >
                  Clear Filters
                </button>
              </div>

            </div>
          </div>

          {/* Table Container */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 relative">
            {reportLoading && (
              <div className="absolute inset-0 bg-white/70 backdrop-blur-xs flex justify-center items-center z-10 rounded-2xl">
                <CircleLoaderComponent />
              </div>
            )}
            <DataTableComponent
              keyField={(row) => row.date}
              columns={columns}
              data={tableDataItems}
              totalRows={totalRows}
              handlePerRowsChange={handlePerRowsChange}
              handlePageChange={handlePageChange}
              paginationDefaultPage={currentPage}
              paginationResetDefaultPage={paginationResetToggle}
            />
          </div>

        </div>
      </div>
    </>
  );
};

export default BchDailyReport;
